import { noItx } from "../../../src/translationItx/itx";
import Button from "../../../src/models/Button";
import BaseModel from "../../../src/models/BaseModel";
import type Channel from "../../../src/models/Channel";
import { ChannelType } from "../../../src/models/ChannelType";
import Consumer from "../../../src/models/Consumer";
import { DeviceClasses } from "../../../src/models/DeviceClasses";
import Group from "../../../src/models/Group";
import Room from "../../../src/models/Room";
import {
  C1_EloItem_PlugType,
  C1_EloItem_Type,
  type C1_EloItemInstanceData,
  type C1_EloItem_Definition,
  type C1_LegacyEloItemTreeData,
} from "../types/C1_EloTypes";
import { C1_Button } from "./C1_Button";
import { C1_Consumer } from "./C1_Consumer";
import { C1_DefinitionItem } from "./C1_DefinitionItem";
import { C1_EloGroup } from "./C1_EloGroup";
import { migrateLegacyEloItemTreeNode } from "./C1_EloItem";
import { createEloItemInstance } from "./C1_ensureEloEquipmentDefaults";
import { isGeneratedPlugItemName } from "../C1_uiLabels";
import { connectOpeningContacts } from "./C1_connectOpeningContacts";
import { getButtonProductDefinition } from "../data/C1_ProductDefinitions";
import { channelTypeForDefinition } from "./C1_eloChannelType";

type EloContainer = Room | Group;

/** Creates one independently placed device using the same definition defaults as grouped equipment. */
export function materializeInteractiveEloItem(room: Room, source: C1_LegacyEloItemTreeData): C1_Button | C1_Consumer | C1_EloGroup {
  if (source.items.length !== 1) throw new Error("An interactive device definition must have exactly one item.");
  const root = state.objectRegistry.getObject<C1_DefinitionItem>(source.definitionId);
  if (!root) throw new Error(`Unknown interactive device definition: ${source.definitionId}`);
  const item = materializeChild(room, root.id, source.items[0]);
  if (!(item?.c1 instanceof C1_Button) && !(item?.c1 instanceof C1_Consumer) && !(item?.c1 instanceof C1_EloGroup)) {
    throw new Error("An interactive device must be a button, consumer or ELO group.");
  }
  item.c1.interactivCreated = true;
  return item.c1;
}

/** Result of applying C1 outlet wiring rules: either a sibling channel to share, or whether a fresh autoconnect is needed. */
type PlugWiringResult = { channel?: Channel; autoConnect: boolean };

/** Applies C1 outlet wiring rules for a newly placed plug consumer. */
function configurePlugWiring(parent: EloContainer, consumer: Consumer, plugType: C1_EloItem_PlugType | undefined): PlugWiringResult {
  if (plugType === C1_EloItem_PlugType.USB || plugType === C1_EloItem_PlugType.TV) {
    consumer.noCable = true;
    return { autoConnect: false };
  }
  if (plugType === C1_EloItem_PlugType.V230) {
    let wiredSibling: Consumer | undefined;
    for (const sibling of parent.consumers) {
      if (sibling === consumer || !(sibling.c1 instanceof C1_Consumer)) continue;
      if (sibling.channelType === ChannelType.plug_230V && !sibling.noCable) {
        wiredSibling = sibling;
        break;
      }
    }
    consumer.noCable = !!wiredSibling;
    if (!wiredSibling) return { autoConnect: true };
    // Siblings sharing one cable are also switched together: reuse the wired sibling's
    // channel instead of leaving this socket without any electrical connection.
    const sharedChannel = wiredSibling.getConnectedChannel();
    return sharedChannel ? { channel: sharedChannel, autoConnect: false } : { autoConnect: true };
  }
  consumer.noCable = false;
  return { autoConnect: true };
}

/** Adds a newly materialized group to its ESTADA parent collection. */
function addGroup(parent: EloContainer, group: Group): void {
  parent.groups.add(group);
}

/** Adds a newly materialized consumer to its ESTADA parent collection. */
function addConsumer(parent: EloContainer, consumer: Consumer): void {
  parent.consumers.add(consumer);
}

/** Adds a newly materialized button to its ESTADA parent collection. */
function addButton(parent: EloContainer, button: Button): void {
  parent.buttons.add(button);
}

/** Automatic outlet names are computed by BaseObject; persist only custom names. */
function materializedItemName(
  source: C1_LegacyEloItemTreeData,
  instanceData: C1_EloItemInstanceData,
  definition: C1_DefinitionItem,
): string {
  const persistedName = source.name?.trim() ?? "";
  if (definition.defaultName === "" &&
    (!persistedName || Object.values(definition.description).includes(persistedName) || definition.generatedNameAliases?.includes(persistedName))) return "";
  const repeatIndex = instanceData.repeatIndex;
  if (definition.type === C1_EloItem_Type.Plug && repeatIndex !== undefined) {
    const generatedNames = new Set(definition.generatedNameAliases ?? []);
    for (const language of ["de", "en"] as const) {
      generatedNames.add(definition.description[language]);
      generatedNames.add(`${definition.description[language]} #${repeatIndex}`);
    }
    if (!persistedName || generatedNames.has(persistedName) || isGeneratedPlugItemName(persistedName, repeatIndex)) return "";
  }
  return persistedName || (definition.defaultName ?? (definition.description[state.c1.language] || definition.definitionId));
}

/** Materializes one definition-backed legacy node below an ESTADA container. */
function materializeChild(
  parent: EloContainer,
  parentDefinitionPath: string,
  source: C1_LegacyEloItemTreeData,
): BaseModel | undefined {
  const instanceData = migrateLegacyEloItemTreeNode(source, parent, parentDefinitionPath);
  const definition = state.objectRegistry.getObject<C1_DefinitionItem>(instanceData._defId);
  if (!definition) {
    console.error(noItx(`[C1 ELO] Definition "${instanceData._defId || source.definitionId}" cannot be resolved.`));
    return undefined;
  }

  const name = materializedItemName(source, instanceData, definition);
  const objectData = { id: source.id, name, tag: source.tag ?? "" };

  if (definition.type === C1_EloItem_Type.Group || definition.type === C1_EloItem_Type.PlugGroup) {
    const group = new Group({ ...objectData, groupType: "elo" }, parent);
    const facet = new C1_EloGroup(instanceData, group);
    group.c1 = facet;
    addGroup(parent, group);
    for (const child of source.items) materializeChild(group, definition.id, child);
    return group;
  }

  if (definition.type === C1_EloItem_Type.Button || definition.type === C1_EloItem_Type.Sensor) {
    const button = new Button(objectData, parent);
    button.classification =
      definition.type === C1_EloItem_Type.Sensor ? DeviceClasses.SensorNew : DeviceClasses.Button;
    const buttonFacet = new C1_Button(instanceData, button);
    button.c1 = buttonFacet;
    addButton(parent, button);
    button.findFreePhysicalAddress();
    const buttonType = instanceData.details?.buttonType ?? definition.defaultDetails?.buttonType;
    const templateGroupTag = getButtonProductDefinition(buttonType)?.uiTemplate ?? "simple";
    const templates = state.templateGroups.findFirst(group => group.templateGroupType === button.classification);
    const favorite = definition.type === C1_EloItem_Type.Sensor
      ? templates?.favoriteTemplate
      : templates?.templateGroups.findFirst(group => group.tag === templateGroupTag)?.favoriteTemplate;
    // setTemplate loads defaults, channels and metadata itself; do not race it with a second update.
    const initialization = favorite ? button.setTemplate(favorite) : button.updateAllFromKnxMetadataImmediately();
    void initialization.catch(error => console.error(noItx("[C1 ELO] Button initialization failed."), error));
    return button;
  }

  const consumer = new Consumer(objectData, parent);
  consumer.channelType = channelTypeForDefinition(definition, instanceData);
  consumer.isDali = instanceData.details?.isDali ?? definition.defaultDetails?.isDali ?? (definition.type === C1_EloItem_Type.Light && consumer.room?.c1?.daliAsDefault === true);
  consumer.c1 = new C1_Consumer(instanceData, consumer);
  addConsumer(parent, consumer);
  const plugType = definition.type === C1_EloItem_Type.Plug ? instanceData.details?.plugType : undefined;
  if (definition.type === C1_EloItem_Type.Plug) consumer.prefixType = consumer.channelType;
  const wiring = definition.type === C1_EloItem_Type.Plug ? configurePlugWiring(parent, consumer, plugType) : undefined;
  const room = consumer.room;
  if (room) {
    if (definition.type === C1_EloItem_Type.Plug) {
      if (wiring?.channel) wiring.channel.connectConsumer(room.getOrCreatePage(), consumer, false);
      else if (wiring?.autoConnect) consumer.autoConnectChannel(room.getOrCreatePage(), undefined, consumer.isDali);
    } else {
      consumer.autoConnectChannel(room.getOrCreatePage(), undefined, consumer.isDali);
    }
  }
  connectOpeningContacts(parent);
  return consumer;
}

/** Materializes one explicitly selected definition and its default descendants. */
export function materializeOptionalDefinitionChild(
  parent: EloContainer,
  definition: C1_DefinitionItem,
  configuredDefinition?: C1_EloItem_Definition,
): BaseModel | undefined {
  const parentDefinition = definition.parent?.parent;
  if (!(parentDefinition instanceof C1_DefinitionItem)) return undefined;

  const source = createEloItemInstance(
    configuredDefinition ?? definition.toDefinition(),
    state.c1.project.equipmentStandard,
    state.c1.project.settings.buttonDefaults,
  );
  return materializeChild(parent, parentDefinition.id, source);
}

/**
 * Creates the complete ESTADA equipment hierarchy for one placed C1 object.
 *
 * The root definition is virtual because the placed room, furniture or opening
 * already owns the root identity. Only its definition children become ESTADA
 * Groups, Consumers and Buttons. A transient root facade is derived separately
 * for traversal and placement calculations.
 */
export function materializeEloDefinitionTree(
  owner: EloContainer,
  source: C1_LegacyEloItemTreeData | undefined,
): void {
  if (!source) return;
  const rootData = migrateLegacyEloItemTreeNode(source, owner, "");
  const rootDefinition = state.objectRegistry.getObject<C1_DefinitionItem>(rootData._defId);
  if (!rootDefinition) {
    console.error(noItx(`[C1 ELO] Root definition "${rootData._defId || source.definitionId}" cannot be resolved.`));
    return;
  }

  for (const child of source.items) materializeChild(owner, rootDefinition.id, child);
}

/**
 * Creates a transient root facade over the ELO facets already stored in the
 * owner's ESTADA group, consumer and button collections.
 */
export function getMaterializedEloRoot(
  owner: EloContainer | Consumer | undefined,
  source: C1_LegacyEloItemTreeData | string | undefined,
): C1_EloGroup | undefined {
  if (!owner || !source) return undefined;
  const rootSource: C1_LegacyEloItemTreeData = typeof source === "string"
    ? { id: owner.id, definitionId: source, items: [] }
    : source;
  const root = new C1_EloGroup(migrateLegacyEloItemTreeNode(rootSource, owner, ""), owner);
  if (owner instanceof Consumer && owner.c1 instanceof C1_Consumer && owner.c1.data.lightGroupFurniture) {
    root.setTemporaryItems([owner.c1]);
  }
  return root;
}

/** Appends new equipment in place; other edits rebuild only the C1 ELO children. */
export function replaceMaterializedEloDefinitionTree(
  owner: EloContainer | undefined,
  source: C1_LegacyEloItemTreeData | undefined,
): void {
  if (!owner) return;
  if (source && appendMaterializedEloDefinitionTree(owner, source)) return;

  const interactiveIds = new Set<string>();
  for (const item of [...owner.consumers.asArray(), ...owner.buttons.asArray(), ...owner.groups.asArray()]) {
    if (item.c1 instanceof C1_Consumer || item.c1 instanceof C1_Button || item.c1 instanceof C1_EloGroup) {
      if (item.c1.interactivCreated) interactiveIds.add(item.id);
    }
  }

  const groups = owner.groups.asArray();
  for (const group of groups) {
    if (group.c1 instanceof C1_EloGroup && !group.c1.interactivCreated) group.delete();
  }

  const consumers = owner.consumers.asArray();
  for (const consumer of consumers) {
    if (consumer.c1 instanceof C1_Consumer && !consumer.c1.interactivCreated) consumer.delete();
  }

  const buttons = owner.buttons.asArray();
  for (const button of buttons) {
    if (button.c1 instanceof C1_Button && !button.c1.interactivCreated) button.delete();
  }

  const definitionSource = source ? { ...source, items: source.items.filter(item => !interactiveIds.has(item.id)) } : undefined;
  materializeEloDefinitionTree(owner, definitionSource);
}

function sameInstanceValue(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (!left || !right || typeof left !== "object" || typeof right !== "object") return false;
  const a = left as Record<string, unknown>;
  const b = right as Record<string, unknown>;
  return [...new Set([...Object.keys(a), ...Object.keys(b)])].every(key => sameInstanceValue(a[key], b[key]));
}

/** Applies additions only after verifying the entire edit leaves existing devices unchanged. */
export function appendMaterializedEloDefinitionTree(owner: EloContainer, source: C1_LegacyEloItemTreeData): boolean {
  const root = getMaterializedEloRoot(owner, source.definitionId);
  if (!root?.definition || root.id !== source.id) return false;
  const additions: Array<{ parent: C1_EloGroup; source: C1_LegacyEloItemTreeData }> = [];
  const counts: Array<{ group: C1_EloGroup; count: number }> = [];
  const ids = new Set<string>();
  const validateNew = (node: C1_LegacyEloItemTreeData, parent: EloContainer, definitionPath: string): boolean => {
    if (ids.has(node.id) || state.objectRegistry.getObject(node.id)) return false;
    ids.add(node.id);
    const data = migrateLegacyEloItemTreeNode(node, parent, definitionPath);
    const childDefinitionPath = data._defId;
    if (!childDefinitionPath || !state.objectRegistry.getObject(childDefinitionPath)) return false;
    return node.items.every(child => validateNew(child, parent, childDefinitionPath));
  };
  const inspect = (current: C1_EloGroup | C1_Consumer | C1_Button, next: C1_LegacyEloItemTreeData): boolean => {
    if (ids.has(next.id)) return false;
    ids.add(next.id);
    const { items: _oldItems, ...before } = current.toLegacyTreeData();
    const { items: _newItems, ...after } = next;
    if (current instanceof C1_EloGroup && current.definition?.details?.repeatCount !== undefined &&
        after.details?.itemCount !== before.details?.itemCount) {
      const count = after.details?.itemCount;
      if (count === undefined || !Number.isInteger(count) || count <= (before.details?.itemCount ?? 0)) return false;
      counts.push({ group: current, count });
      after.details = { ...after.details, itemCount: before.details?.itemCount };
    }
    if (!sameInstanceValue(before, after)) return false;
    if (!(current instanceof C1_EloGroup)) return next.items.length === 0;
    const children = current.items;
    // Missing/reordered nodes or changed device settings require the regular editing path.
    const existingIds = new Set(children.map(child => child.id));
    const retained = next.items.filter(child => existingIds.has(child.id));
    if (retained.length !== children.length || retained.some((child, i) => child.id !== children[i].id)) return false;
    for (const child of next.items) {
      const existing = children.find(item => item.id === child.id);
      if (existing) {
        if (!inspect(existing as C1_EloGroup | C1_Consumer | C1_Button, child)) return false;
      } else {
        if (!current.owner || !validateNew(child, current.owner, current._defId)) return false;
        additions.push({ parent: current, source: child });
      }
    }
    return true;
  };
  if (!inspect(root, source) || (counts.length > 0 && additions.length === 0)) return false;
  for (const { parent, source: child } of additions) materializeChild(parent.owner!, parent._defId, child);
  for (const { group, count } of counts) group.setItemCount(count);
  return true;
}
